import { createFileRoute } from '@tanstack/react-router';
import {
    BadgeDollarSign,
    Beaker,
    ExternalLink,
    Loader2,
    PhoneCall,
    RefreshCw,
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { AdminModeBadge } from '@apps/web/components/admin/admin_mode_badge';
import { Badge } from '@apps/web/components/ui/badge';
import { Button } from '@apps/web/components/ui/button';
import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
} from '@apps/web/components/ui/card';
import { Input } from '@apps/web/components/ui/input';
import { Label } from '@apps/web/components/ui/label';
import { ScrollArea } from '@apps/web/components/ui/scroll_area';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@apps/web/components/ui/select';
import { Separator } from '@apps/web/components/ui/separator';
import { Skeleton } from '@apps/web/components/ui/skeleton';
import { Textarea } from '@apps/web/components/ui/textarea';
import { VoiceCallDetailPane } from '@apps/web/components/voice_calls/voice_call_detail_pane';
import { useAdminMode } from '@apps/web/lib/admin_mode_store';
import { getApiClient } from '@apps/web/lib/api_client';
import { toast } from '@apps/web/lib/app_notifications';
import {
    useAdminOrganizations,
    useAdminVoiceCall,
} from '@apps/web/swr_data_fetching/data_fetching_hooks';

const PROMPT_STORAGE_KEY = 'voice-admin-lab:prompt';
const NOTES_STORAGE_KEY = 'voice-admin-lab:notes';
const ORG_STORAGE_KEY = 'voice-admin-lab:org';
const PHONE_STORAGE_KEY = 'voice-admin-lab:phone-number';
const GREETING_STORAGE_KEY = 'voice-admin-lab:initial-greeting';
const SHIFT_DETAILS_STORAGE_KEY = 'voice-admin-lab:shift-details';

type PipecatVoicePreset = {
    readonly id: string;
    readonly label: string;
    readonly ttsService: 'cartesia' | 'elevenlabs';
    readonly ttsVoiceId: string;
};

const PIPECAT_VOICE_PRESETS: Array<PipecatVoicePreset> = [
    {
        id: 'elevenlabs-default',
        label: 'ElevenLabs · Default Test Voice',
        ttsService: 'elevenlabs',
        ttsVoiceId: 'cgSgspJ2msm6clMCkdW9',
    },
    {
        id: 'elevenlabs-rachel',
        label: 'ElevenLabs · Rachel',
        ttsService: 'elevenlabs',
        ttsVoiceId: '21m00Tcm4TlvDq8ikWAM',
    },
];

type PromptPreview = {
    readonly prompt: string;
    readonly variables: Record<string, unknown>;
};

type ShiftDetailsForm = {
    readonly agency_name: string;
    readonly caregiver_first_name: string;
    readonly shift_date: string;
    readonly shift_start_time: string;
    readonly shift_end_time: string;
    readonly shift_location: string;
    readonly shift_city: string;
    readonly shift_state: string;
    readonly role_required: string;
    readonly instructions: string;
    readonly patient_identifier: string;
};

const DEFAULT_SHIFT_DETAILS: ShiftDetailsForm = {
    agency_name: 'Test Agency',
    caregiver_first_name: 'John',
    shift_date: '2024-01-15',
    shift_start_time: '08:00 AM',
    shift_end_time: '04:00 PM',
    shift_location: '123 Main St, Anytown, USA',
    shift_city: 'Springfield',
    shift_state: 'MA',
    role_required: 'caregiver',
    instructions:
        'Patient is a fall risk; remind them to review the care plan.',
    patient_identifier: 'a patient',
};

export const Route = createFileRoute('/_app/admin/voice')({
    head: () => ({
        meta: [
            {
                title: 'Voice Agent Lab | Phoebe Admin',
            },
        ],
    }),
    component: VoiceAgentLabPage,
});

function VoiceAgentLabPage() {
    const adminMode = useAdminMode();
    const { data: organizationsResponse, isLoading: isLoadingOrganizations } =
        useAdminOrganizations({ page: 1, per_page: 200 });

    const [phoneNumber, setPhoneNumber] = useState('');
    const [prompt, setPrompt] = useState('');
    const [experimentLabel, setExperimentLabel] = useState('');
    const [notes, setNotes] = useState('');
    const [selectedPresetId, setSelectedPresetId] = useState(
        PIPECAT_VOICE_PRESETS[0]?.id ?? '',
    );
    const [selectedOrganizationId, setSelectedOrganizationId] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [activeCallId, setActiveCallId] = useState<string | null>(null);
    const [promptPreview, setPromptPreview] = useState<PromptPreview | null>(
        null,
    );
    const [isPromptLoading, setIsPromptLoading] = useState(false);
    const [initialGreeting, setInitialGreeting] = useState('');
    const [shiftDetails, setShiftDetails] = useState<ShiftDetailsForm>(
        DEFAULT_SHIFT_DETAILS,
    );

    const isMountedRef = useRef(false);

    const { data: activeCall, mutate: refreshActiveCall } =
        useAdminVoiceCall(activeCallId);

    useEffect(() => {
        if (typeof window === 'undefined') {
            return;
        }
        isMountedRef.current = true;

        const savedPrompt = window.localStorage.getItem(PROMPT_STORAGE_KEY);
        if (savedPrompt) {
            setPrompt(savedPrompt);
        }

        const savedNotes = window.localStorage.getItem(NOTES_STORAGE_KEY);
        if (savedNotes) {
            setNotes(savedNotes);
        }

        const savedPhone = window.localStorage.getItem(PHONE_STORAGE_KEY);
        if (savedPhone) {
            setPhoneNumber(savedPhone);
        }

        const savedGreeting = window.localStorage.getItem(GREETING_STORAGE_KEY);
        if (savedGreeting) {
            setInitialGreeting(savedGreeting);
        }

        const savedOrg = window.localStorage.getItem(ORG_STORAGE_KEY);
        if (savedOrg) {
            setSelectedOrganizationId(savedOrg);
        }

        const savedShiftDetails = window.localStorage.getItem(
            SHIFT_DETAILS_STORAGE_KEY,
        );
        if (savedShiftDetails) {
            try {
                const parsed = JSON.parse(
                    savedShiftDetails,
                ) as Partial<ShiftDetailsForm>;
                setShiftDetails({ ...DEFAULT_SHIFT_DETAILS, ...parsed });
            } catch (error) {
                console.error('Failed to parse saved shift details', error);
            }
        }

        return () => {
            isMountedRef.current = false;
        };
    }, []);

    useEffect(() => {
        const fetchPromptPreview = async () => {
            setIsPromptLoading(true);
            try {
                const response = await getApiClient().get({
                    url: '/admin/pipecat/prompts/{agent}',
                    pathParams: { agent: 'callout_multi_shift' },
                });
                if (!isMountedRef.current) {
                    return;
                }
                setPromptPreview({
                    prompt: response.prompt,
                    variables: response.variables,
                });
                setPrompt((existing) =>
                    existing.trim().length > 0 ? existing : response.prompt,
                );
                setInitialGreeting((existing) => {
                    if (existing.trim().length > 0) {
                        return existing;
                    }
                    const vars = response.variables as Record<string, unknown>;
                    const caregiver =
                        (vars['caregiver_first_name'] as string | undefined) ??
                        'there';
                    const agency =
                        (vars['agency_name'] as string | undefined) ??
                        'your agency';
                    return `Hi ${caregiver}, it's Phoebe with ${agency}. I'm calling to see if you are interested in picking up a shift - can I tell you more?`;
                });
            } catch (error) {
                toast.error('Failed to load the latest prompt template', {
                    persistNotification: false,
                });
            } finally {
                if (isMountedRef.current) {
                    setIsPromptLoading(false);
                }
            }
        };

        void fetchPromptPreview();
    }, []);

    useEffect(() => {
        if (typeof window === 'undefined') {
            return;
        }
        window.localStorage.setItem(PROMPT_STORAGE_KEY, prompt);
    }, [prompt]);

    useEffect(() => {
        if (typeof window === 'undefined') {
            return;
        }
        window.localStorage.setItem(NOTES_STORAGE_KEY, notes);
    }, [notes]);

    useEffect(() => {
        if (typeof window === 'undefined') {
            return;
        }
        window.localStorage.setItem(PHONE_STORAGE_KEY, phoneNumber);
    }, [phoneNumber]);

    useEffect(() => {
        if (typeof window === 'undefined') {
            return;
        }
        if (initialGreeting.trim().length > 0) {
            window.localStorage.setItem(GREETING_STORAGE_KEY, initialGreeting);
        } else {
            window.localStorage.removeItem(GREETING_STORAGE_KEY);
        }
    }, [initialGreeting]);

    useEffect(() => {
        if (typeof window === 'undefined') {
            return;
        }
        window.localStorage.setItem(
            SHIFT_DETAILS_STORAGE_KEY,
            JSON.stringify(shiftDetails),
        );
    }, [shiftDetails]);

    useEffect(() => {
        if (typeof window === 'undefined') {
            return;
        }
        if (selectedOrganizationId) {
            window.localStorage.setItem(
                ORG_STORAGE_KEY,
                selectedOrganizationId,
            );
        }
    }, [selectedOrganizationId]);

    useEffect(() => {
        if (!activeCallId) {
            return;
        }
        void refreshActiveCall();
    }, [activeCallId, refreshActiveCall]);

    useEffect(() => {
        if (!activeCallId) {
            return;
        }
        if (activeCall?.status === 'ended') {
            return;
        }

        const interval = window.setInterval(() => {
            void refreshActiveCall();
        }, 3000);

        return () => {
            window.clearInterval(interval);
        };
    }, [activeCall?.status, activeCallId, refreshActiveCall]);

    const organizations = useMemo(
        () => organizationsResponse?.organizations ?? [],
        [organizationsResponse],
    );

    useEffect(() => {
        if (selectedOrganizationId || organizations.length === 0) {
            return;
        }
        const firstOrgId = organizations[0]?.id;
        if (firstOrgId) {
            setSelectedOrganizationId(firstOrgId);
        }
    }, [organizations, selectedOrganizationId]);

    const selectedPreset = useMemo(
        () =>
            PIPECAT_VOICE_PRESETS.find(
                (preset) => preset.id === selectedPresetId,
            ) ?? PIPECAT_VOICE_PRESETS[0],
        [selectedPresetId],
    );
    const isCreateTestCallDisabled = isSubmitting || adminMode === 'sandbox';

    const handleTriggerCall = useCallback(async () => {
        if (adminMode === 'sandbox') {
            return;
        }
        if (!phoneNumber.trim()) {
            toast.error('Enter a phone number to call', {
                persistNotification: false,
            });
            return;
        }
        if (!selectedOrganizationId) {
            toast.error('Select an organization', {
                persistNotification: false,
            });
            return;
        }
        if (!selectedPreset) {
            toast.error('Select a TTS preset', { persistNotification: false });
            return;
        }

        setIsSubmitting(true);
        try {
            const response = await getApiClient().post({
                url: '/admin/pipecat/create-test-call',
                queryParams: { mode: adminMode },
                mode: adminMode,
                body: {
                    phone_number: phoneNumber,
                    organization_id: selectedOrganizationId,
                    prompt_override: prompt,
                    experiment_label: experimentLabel || undefined,
                    notes: notes || undefined,
                    initial_greeting_override:
                        initialGreeting.trim().length > 0
                            ? initialGreeting
                            : undefined,
                    shift_details: shiftDetails,
                    tts_service: selectedPreset.ttsService,
                    tts_voice_id: selectedPreset.ttsVoiceId,
                },
            });

            if (!response?.call_id) {
                throw new Error('Missing call id in response');
            }

            setActiveCallId(response.call_id);
            toast.success('Voice call initiated', {
                persistNotification: false,
            });
        } catch (error) {
            toast.error('Failed to initiate voice call', {
                persistNotification: false,
            });
        } finally {
            setIsSubmitting(false);
        }
    }, [
        adminMode,
        experimentLabel,
        initialGreeting,
        notes,
        phoneNumber,
        prompt,
        selectedOrganizationId,
        selectedPreset,
        shiftDetails,
    ]);

    return (
        <div className="flex h-full flex-col">
            <div className="border-b px-6 py-4">
                <div className="flex flex-col gap-2 lg:flex-row lg:items-center lg:justify-between">
                    <div>
                        <h1 className="text-2xl font-semibold tracking-tight">
                            Voice Agent Lab
                        </h1>
                        <p className="text-muted-foreground text-sm">
                            Iterate on the open shift prompt, place a live call,
                            then review the transcript and recording in-line.
                        </p>
                    </div>
                    <div className="text-muted-foreground flex items-center gap-2 text-sm">
                        <Beaker className="h-4 w-4" />
                        Internal tooling · Calls are tagged as experiments
                    </div>
                </div>
            </div>
            <div className="flex flex-1 flex-col gap-6 px-6 py-6 lg:flex-row">
                <div className="flex w-full flex-col gap-6 lg:max-w-md">
                    <Card>
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2 text-lg">
                                <PhoneCall className="h-5 w-5" />
                                Configure Call
                            </CardTitle>
                            <CardDescription>
                                Fill in the caller details, tweak the prompt,
                                and launch a single test call.
                            </CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-5">
                            <div className="space-y-2">
                                <Label htmlFor="phone-number">
                                    Phone number
                                </Label>
                                <Input
                                    id="phone-number"
                                    type="tel"
                                    placeholder="+15551234567"
                                    value={phoneNumber}
                                    onChange={(event) =>
                                        setPhoneNumber(event.target.value)
                                    }
                                />
                                <p className="text-muted-foreground text-xs">
                                    We’ll normalize to E.164 before dialing. Use
                                    your cellphone or a test device.
                                </p>
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="organization">
                                    Organization
                                </Label>
                                <Select
                                    value={selectedOrganizationId}
                                    onValueChange={setSelectedOrganizationId}
                                    disabled={isLoadingOrganizations}
                                >
                                    <SelectTrigger id="organization">
                                        <SelectValue placeholder="Select an organization" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {isLoadingOrganizations ? (
                                            <SelectItem
                                                value="loading"
                                                disabled={true}
                                            >
                                                Loading organizations…
                                            </SelectItem>
                                        ) : organizations.length > 0 ? (
                                            organizations.map((org) => (
                                                <SelectItem
                                                    key={org.id}
                                                    value={org.id}
                                                >
                                                    {org.name}
                                                </SelectItem>
                                            ))
                                        ) : (
                                            <SelectItem
                                                value="none"
                                                disabled={true}
                                            >
                                                No organizations available
                                            </SelectItem>
                                        )}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="tts-preset">Voice preset</Label>
                                <Select
                                    value={selectedPresetId}
                                    onValueChange={setSelectedPresetId}
                                >
                                    <SelectTrigger id="tts-preset">
                                        <SelectValue placeholder="Select a voice" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {PIPECAT_VOICE_PRESETS.map((preset) => (
                                            <SelectItem
                                                key={preset.id}
                                                value={preset.id}
                                            >
                                                {preset.label}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <Separator />
                            <div className="space-y-2">
                                <Label htmlFor="experiment-label">
                                    Experiment label (optional)
                                </Label>
                                <Input
                                    id="experiment-label"
                                    placeholder="eg. Pause-before-direct-question"
                                    value={experimentLabel}
                                    onChange={(event) =>
                                        setExperimentLabel(event.target.value)
                                    }
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="notes">Notes (optional)</Label>
                                <Textarea
                                    id="notes"
                                    placeholder="Context for this experiment — why we are trying this prompt, expectations, etc."
                                    value={notes}
                                    onChange={(event) =>
                                        setNotes(event.target.value)
                                    }
                                    rows={3}
                                />
                            </div>
                            <Button
                                className="w-full"
                                onClick={handleTriggerCall}
                                disabled={isCreateTestCallDisabled}
                            >
                                {isSubmitting ? (
                                    <span className="flex items-center justify-center gap-2">
                                        <Loader2 className="h-4 w-4 animate-spin" />
                                        Placing call…
                                    </span>
                                ) : (
                                    <span className="flex items-center justify-center gap-2">
                                        <PhoneCall className="h-4 w-4" />
                                        Place test call
                                    </span>
                                )}
                            </Button>
                            <div className="flex flex-wrap items-center justify-end gap-2">
                                <AdminModeBadge mode={adminMode} />
                                {adminMode === 'sandbox' && (
                                    <p className="text-muted-foreground text-xs">
                                        Test calls dial real telephony and
                                        require live mode.
                                    </p>
                                )}
                            </div>
                            {activeCallId && (
                                <div className="text-muted-foreground rounded-md border border-dashed p-3 text-xs">
                                    <div className="flex items-center gap-2">
                                        <Badge
                                            variant="secondary"
                                            className="gap-1 text-xs"
                                        >
                                            <RefreshCw className="h-3 w-3" />
                                            Monitoring
                                        </Badge>
                                        <span className="font-medium">
                                            Call ID:
                                        </span>
                                        <code className="bg-muted rounded px-1 py-0.5">
                                            {activeCallId}
                                        </code>
                                    </div>
                                    <p className="mt-2">
                                        Head to the{' '}
                                        <a
                                            className="text-primary inline-flex items-center gap-1 underline-offset-4 hover:underline"
                                            href="/admin/voice-qa"
                                            target="_blank"
                                            rel="noreferrer"
                                        >
                                            Voice Calls history
                                            <ExternalLink className="h-3 w-3" />
                                        </a>{' '}
                                        for cross-reference or leave this tab
                                        open to watch the transcript stream in.
                                    </p>
                                </div>
                            )}
                        </CardContent>
                    </Card>
                    <Card>
                        <CardHeader>
                            <CardTitle className="text-base">
                                Shift details
                            </CardTitle>
                            <CardDescription>
                                Override the values passed to the agent about
                                this shift.
                            </CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div className="grid gap-4">
                                <div className="space-y-1">
                                    <Label htmlFor="agency-name">
                                        Agency name
                                    </Label>
                                    <Input
                                        id="agency-name"
                                        value={shiftDetails.agency_name}
                                        onChange={(event) =>
                                            setShiftDetails((prev) => ({
                                                ...prev,
                                                agency_name: event.target.value,
                                            }))
                                        }
                                    />
                                </div>
                                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                                    <div className="space-y-1">
                                        <Label htmlFor="caregiver-first-name">
                                            Caregiver first name
                                        </Label>
                                        <Input
                                            id="caregiver-first-name"
                                            value={
                                                shiftDetails.caregiver_first_name
                                            }
                                            onChange={(event) =>
                                                setShiftDetails((prev) => ({
                                                    ...prev,
                                                    caregiver_first_name:
                                                        event.target.value,
                                                }))
                                            }
                                        />
                                    </div>
                                    <div className="space-y-1">
                                        <Label htmlFor="role-required">
                                            Role required
                                        </Label>
                                        <Input
                                            id="role-required"
                                            value={shiftDetails.role_required}
                                            onChange={(event) =>
                                                setShiftDetails((prev) => ({
                                                    ...prev,
                                                    role_required:
                                                        event.target.value,
                                                }))
                                            }
                                        />
                                    </div>
                                </div>
                                <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                                    <div className="space-y-1">
                                        <Label htmlFor="shift-date">
                                            Shift date
                                        </Label>
                                        <Input
                                            id="shift-date"
                                            value={shiftDetails.shift_date}
                                            onChange={(event) =>
                                                setShiftDetails((prev) => ({
                                                    ...prev,
                                                    shift_date:
                                                        event.target.value,
                                                }))
                                            }
                                        />
                                    </div>
                                    <div className="space-y-1">
                                        <Label htmlFor="shift-start-time">
                                            Start time
                                        </Label>
                                        <Input
                                            id="shift-start-time"
                                            value={
                                                shiftDetails.shift_start_time
                                            }
                                            onChange={(event) =>
                                                setShiftDetails((prev) => ({
                                                    ...prev,
                                                    shift_start_time:
                                                        event.target.value,
                                                }))
                                            }
                                        />
                                    </div>
                                    <div className="space-y-1">
                                        <Label htmlFor="shift-end-time">
                                            End time
                                        </Label>
                                        <Input
                                            id="shift-end-time"
                                            value={shiftDetails.shift_end_time}
                                            onChange={(event) =>
                                                setShiftDetails((prev) => ({
                                                    ...prev,
                                                    shift_end_time:
                                                        event.target.value,
                                                }))
                                            }
                                        />
                                    </div>
                                </div>
                                <div className="space-y-1">
                                    <Label htmlFor="shift-location">
                                        Shift location
                                    </Label>
                                    <Input
                                        id="shift-location"
                                        value={shiftDetails.shift_location}
                                        onChange={(event) =>
                                            setShiftDetails((prev) => ({
                                                ...prev,
                                                shift_location:
                                                    event.target.value,
                                            }))
                                        }
                                    />
                                </div>
                                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                                    <div className="space-y-1">
                                        <Label htmlFor="shift-city">City</Label>
                                        <Input
                                            id="shift-city"
                                            value={shiftDetails.shift_city}
                                            onChange={(event) =>
                                                setShiftDetails((prev) => ({
                                                    ...prev,
                                                    shift_city:
                                                        event.target.value,
                                                }))
                                            }
                                        />
                                    </div>
                                    <div className="space-y-1">
                                        <Label htmlFor="shift-state">
                                            State
                                        </Label>
                                        <Input
                                            id="shift-state"
                                            value={shiftDetails.shift_state}
                                            onChange={(event) =>
                                                setShiftDetails((prev) => ({
                                                    ...prev,
                                                    shift_state:
                                                        event.target.value,
                                                }))
                                            }
                                        />
                                    </div>
                                </div>
                                <div className="space-y-1">
                                    <Label htmlFor="patient-identifier">
                                        Patient identifier
                                    </Label>
                                    <Input
                                        id="patient-identifier"
                                        value={shiftDetails.patient_identifier}
                                        onChange={(event) =>
                                            setShiftDetails((prev) => ({
                                                ...prev,
                                                patient_identifier:
                                                    event.target.value,
                                            }))
                                        }
                                    />
                                </div>
                                <div className="space-y-1">
                                    <Label htmlFor="instructions">
                                        Special instructions
                                    </Label>
                                    <Textarea
                                        id="instructions"
                                        className="resize-y"
                                        value={shiftDetails.instructions}
                                        onChange={(event) =>
                                            setShiftDetails((prev) => ({
                                                ...prev,
                                                instructions:
                                                    event.target.value,
                                            }))
                                        }
                                        rows={3}
                                    />
                                </div>
                            </div>
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() =>
                                    setShiftDetails(DEFAULT_SHIFT_DETAILS)
                                }
                            >
                                Reset shift defaults
                            </Button>
                        </CardContent>
                    </Card>
                    <Card className="hidden lg:block">
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2 text-base">
                                <BadgeDollarSign className="h-4 w-4" />
                                Prompt variables reference
                            </CardTitle>
                            <CardDescription>
                                Example payload sent to the voice pipeline when
                                no open shift context overrides it.
                            </CardDescription>
                        </CardHeader>
                        <CardContent>
                            {promptPreview ? (
                                <ScrollArea className="bg-muted/40 h-48 rounded-md border p-3 text-xs">
                                    <pre className="text-[11px] leading-snug whitespace-pre-wrap">
                                        {JSON.stringify(
                                            promptPreview.variables,
                                            null,
                                            2,
                                        )}
                                    </pre>
                                </ScrollArea>
                            ) : (
                                <Skeleton className="h-40 w-full" />
                            )}
                        </CardContent>
                    </Card>
                </div>
                <div className="flex min-h-0 flex-1 flex-col gap-4">
                    <Card>
                        <CardHeader className="flex flex-row items-center justify-between gap-4">
                            <div>
                                <CardTitle className="text-lg">
                                    Prompt editor
                                </CardTitle>
                                <CardDescription>
                                    Changes here apply only to this test call.
                                    We store the draft locally so it’s easy to
                                    iterate.
                                </CardDescription>
                            </div>
                            <Button
                                size="sm"
                                variant="outline"
                                disabled={isPromptLoading || !promptPreview}
                                onClick={() => {
                                    if (!promptPreview) {
                                        return;
                                    }
                                    setPrompt(promptPreview.prompt);
                                    toast.success('Reset prompt to template', {
                                        persistNotification: false,
                                    });
                                }}
                            >
                                {isPromptLoading ? (
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                    'Reset to template'
                                )}
                            </Button>
                        </CardHeader>
                        <CardContent className="flex flex-col gap-4">
                            {promptPreview &&
                                prompt !== promptPreview.prompt && (
                                    <div className="rounded-md border border-dashed border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                                        Using existing prompt edits
                                    </div>
                                )}
                            {isPromptLoading && !prompt ? (
                                <Skeleton className="h-48 w-full" />
                            ) : (
                                <Textarea
                                    className="h-[600px] min-h-[400px] resize-y font-mono text-xs"
                                    value={prompt}
                                    onChange={(event) =>
                                        setPrompt(event.target.value)
                                    }
                                />
                            )}
                            <div className="space-y-2">
                                <div className="flex items-center justify-between">
                                    <Label htmlFor="initial-greeting">
                                        Initial greeting override (optional)
                                    </Label>
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => {
                                            if (!promptPreview) {
                                                setInitialGreeting('');
                                                return;
                                            }
                                            const vars =
                                                promptPreview.variables as Record<
                                                    string,
                                                    unknown
                                                >;
                                            const caregiver =
                                                (vars[
                                                    'caregiver_first_name'
                                                ] as string | undefined) ??
                                                'there';
                                            const agency =
                                                (vars['agency_name'] as
                                                    | string
                                                    | undefined) ??
                                                'your agency';
                                            setInitialGreeting(
                                                `Hi ${caregiver}, it's Phoebe with ${agency}. I'm calling to see if you are interested in picking up a shift - can I tell you more?`,
                                            );
                                            toast.success(
                                                'Reset greeting to template',
                                                { persistNotification: false },
                                            );
                                        }}
                                        disabled={isPromptLoading}
                                    >
                                        Reset to template
                                    </Button>
                                </div>
                                <Textarea
                                    id="initial-greeting"
                                    className="h-[200px] min-h-[120px] resize-y"
                                    value={initialGreeting}
                                    onChange={(event) =>
                                        setInitialGreeting(event.target.value)
                                    }
                                    placeholder="Hi Jordan, it's Phoebe with Sample Home Care..."
                                />
                                <p className="text-muted-foreground text-xs">
                                    Leave blank to use the default scripted
                                    opening.
                                </p>
                            </div>
                            {initialGreeting.trim().length > 0 && (
                                <div className="rounded-md border border-dashed border-green-300 bg-green-50 px-3 py-2 text-xs text-green-900">
                                    Custom greeting will play before the model
                                    speaks.
                                </div>
                            )}
                        </CardContent>
                    </Card>
                    <Card className="overflow-hidden">
                        <CardHeader className="border-b">
                            <CardTitle className="flex items-center gap-2 text-lg">
                                <Beaker className="h-5 w-5" />
                                Call output
                            </CardTitle>
                            <CardDescription>
                                Transcript, developer timeline, and recording
                                once the call connects.
                            </CardDescription>
                        </CardHeader>
                        <CardContent className="min-h-[300px] p-0">
                            <VoiceCallDetailPane
                                call={activeCall ?? null}
                                initialTranscript={activeCall?.transcript}
                                callId={activeCallId}
                            />
                        </CardContent>
                    </Card>
                </div>
            </div>
        </div>
    );
}
